import type {
  RouteMonitoringVisit,
  RouteMonitoringVisitShipping,
} from '@/pages/Business/pages/Monitoring/models';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models';
import { VisitType } from '@/pages/Business/pages/Shippings/models/routing.types';

export const trimMonitoringText = (
  value: string | null | undefined
): string => {
  if (value == null) return '';
  return String(value).trim();
};

export const firstShipping = (
  visit: RouteMonitoringVisit
): RouteMonitoringVisitShipping | undefined => {
  const list = visit.shippings;
  if (!list || list.length === 0) return undefined;
  return list[0];
};

export const visitTypeFromStrings = (
  raw: string | null | undefined
): VisitType => {
  if (raw === 'PICKUP' || raw === VisitType.PICKUP) return VisitType.PICKUP;
  return VisitType.DELIVERY;
};

export const buildCommerceShippingNumber = (
  visit: RouteMonitoringVisit,
  primary: RouteMonitoringVisitShipping | undefined
): string => {
  const fromVisitExternal = trimMonitoringText(visit.externalId);
  if (fromVisitExternal) return fromVisitExternal;
  if (primary) {
    const fromCommercial = trimMonitoringText(primary.commercialShippingNumber);
    if (fromCommercial) return fromCommercial;
    const fromCommerce = trimMonitoringText(primary.commerceShippingNumber);
    if (fromCommerce) return fromCommerce;
  }
  return '';
};

export const buildRecipient = (
  visit: RouteMonitoringVisit,
  primary: RouteMonitoringVisitShipping | undefined
): string => {
  if (primary) {
    const r = trimMonitoringText(primary.recipient);
    if (r) return r;
  }
  const fromVisit = trimMonitoringText(visit.recipient);
  if (fromVisit) return fromVisit;
  return trimMonitoringText(visit.recipientName);
};

export const buildCommerce = (
  visit: RouteMonitoringVisit,
  primary: RouteMonitoringVisitShipping | undefined
): ShippingForRouting['commerce'] => {
  if (primary?.commerce?.name) {
    const name = trimMonitoringText(primary.commerce.name);
    if (name) {
      return {
        id: trimMonitoringText(primary.commerce.id) || '',
        name,
      };
    }
  }
  if (visit.commerce?.name) {
    const name = trimMonitoringText(visit.commerce.name);
    if (name) {
      return {
        id: trimMonitoringText(visit.commerce.id) || '',
        name,
      };
    }
  }
  return null;
};

export const buildCommercialService = (
  primary: RouteMonitoringVisitShipping | undefined
): ShippingForRouting['commercialService'] => {
  if (!primary) return null;

  if (primary.commercialService) {
    const cs = primary.commercialService;
    const commercialName =
      trimMonitoringText(cs.commercialName) || trimMonitoringText(cs.name);
    const name = trimMonitoringText(cs.name) || commercialName;
    if (commercialName || name) {
      return {
        id: trimMonitoringText(cs.id) || '—',
        name: name || commercialName,
        commercialName: commercialName || name,
      };
    }
  }

  const fromSwo = primary.serviceWithOperator?.commercialService;
  if (fromSwo) {
    const commercialName =
      trimMonitoringText(fromSwo.commercialName) ||
      trimMonitoringText(fromSwo.name);
    const name = trimMonitoringText(fromSwo.name) || commercialName;
    if (commercialName || name) {
      return {
        id: trimMonitoringText(fromSwo.id) || '—',
        name: name || commercialName,
        commercialName: commercialName || name,
      };
    }
  }

  const sn = trimMonitoringText(primary.serviceName);
  const scn = trimMonitoringText(primary.serviceCommercialName);
  if (sn || scn) {
    return { id: '—', name: sn || scn, commercialName: scn || sn };
  }

  const svn = trimMonitoringText(primary.serviceVisitTypeName);
  if (svn) {
    return { id: '—', name: svn, commercialName: svn };
  }
  const st = trimMonitoringText(primary.serviceType);
  if (st) {
    return { id: '—', name: st, commercialName: st };
  }
  return null;
};

export const buildLogisticOperator = (
  primary: RouteMonitoringVisitShipping | undefined
): ShippingForRouting['logisticOperator'] => {
  if (!primary?.logisticOperator) return null;
  const name = trimMonitoringText(primary.logisticOperator.name);
  const businessName = primary.logisticOperator.businessName;
  const bn = businessName != null ? trimMonitoringText(businessName) : '';
  if (!name && !bn) return null;
  return {
    name: name || bn,
    businessName: bn || null,
  };
};
